import React from 'react';
import { I18n } from '@aws-amplify/core';
import { Row, Col, Form, message } from 'antd';
import { Button } from '@/components/ui/Button';

import { useOWASContext } from '../../context';
import { useWorkstations, useLines } from '@/hooks/v1';
import { useUpdateOWAS } from '../../hooks/useUpdateOwas';
import { Paragraph, Title } from '@/components/Typography';
import { UpdateOWASReport } from '@/core/mappers/owas-report';
import { useListCompanies } from '@/hooks/v1/useListCompanies';
import { useOrganization, useGetActivities } from '@/hooks';
import { useSectors } from '@/hooks/v1/useSectors';
import {
	Sector,
	Line,
	Company,
	Activity,
	Evaluator,
	ReportName,
	Workstation,
	AnalysisDate,
	Organization,
	NumberExposed,
	CollectionDate,
	CaptureInterval,
	ActivityDescription
} from './Inputs';
import type { FormData } from '@/views/ListReport/Report/Owas/types';

const { useFormInstance, useWatch } = Form;

export function Informations(): JSX.Element {
	const form = useFormInstance<FormData>();
	const { handleNextStep, data, parameters } = useOWASContext();
	const { isLoading, mutateAsync: updateOWASReport } = useUpdateOWAS();

	const formFieldName = 'informations';
	const sector_id = useWatch([formFieldName, 'sector_id'], form);
	const line_id = useWatch([formFieldName, 'line_id'], form);
	const workstation_id = useWatch([formFieldName, 'workstation_id'], form);

	const { organizations: organizationsState } = useOrganization();
	const isLoadingGetOrganizations = organizationsState.loading;
	const organizations = organizationsState.data;
	const isErrorGetOrganizations = !!organizationsState.errors;

	const {
		isLoading: isLoadingGetCompanies,
		data: companies,
		isError: isErrorGetCompanies
	} = useListCompanies({
		organization_id: parameters?.organization_id,
		company_id: parameters?.company_id
	});

	const {
		isLoading: isLoadingGetSectors,
		data: sectors,
		isError: isErrorGetSectors
	} = useSectors({
		organizationId: parameters.organization_id,
		companyId: parameters.company_id
	});

	const {
		isLoading: isLoadingGetLines,
		data: lines,
		isError: isErrorGetLines
	} = useLines({
		organization_id: parameters.organization_id,
		company_id: parameters.company_id,
		sector_id
	});

	const {
		isLoading: isLoadingGetWorkstations,
		data: workstations,
		isError: isErrorGetWorkstations
	} = useWorkstations({
		organization_id: parameters.organization_id,
		company_id: parameters.company_id,
		line_id
	});

	const {
		isLoading: isLoadingGetActivities,
		data: activities,
		isError: isErrorGetActivities
	} = useGetActivities({
		organization_id: parameters.organization_id,
		company_id: parameters.company_id,
		workstation_id
	});

	async function handleOnSubmit(): Promise<void> {
		try {
			const { informations } = await form.validateFields();
			const payload = UpdateOWASReport.toDTO({ informations });
			await updateOWASReport({ id: data?.id!, ...payload });
			handleNextStep();
		} catch (error: any) {
			const { errorFields } = error || {};
			const errorMessage = error.response?.data?.message ?? error.message ?? errorFields[0]?.errors[0];
			message.error(I18n.get(errorMessage));
		}
	}

	return (
		<Row gutter={[0, 40]}>
			<Col span={24}>
				<Row align="middle">
					<Col xs={12}>
						<Title level={4}>{I18n.get('General informations')}</Title>
					</Col>
					<Col xs={24} style={{ marginBottom: '20px' }}>
						<Paragraph>{I18n.get('Enter the data related to the new analysis below')}.</Paragraph>
					</Col>
				</Row>
			</Col>
			<Col span={24}>
				<Row gutter={[0, 90]}>
					<Col span={24}>
						<Row justify="center">
							<Col lg={20} xl={14} xxl={14}>
								<Row gutter={[0, 30]}>
									<ReportName fieldName={formFieldName} />
								</Row>
							</Col>
						</Row>
					</Col>
					<Col span={24}>
						<Row justify="center">
							<Col lg={20} xl={14} xxl={14}>
								<Row gutter={[0, 40]}>
									<Organization
										data={organizations}
										fieldName={formFieldName}
										isError={isErrorGetOrganizations}
										isLoading={isLoadingGetOrganizations}
									/>
									<Company
										data={companies}
										fieldName={formFieldName}
										isError={isErrorGetCompanies}
										isLoading={isLoadingGetCompanies}
									/>
									<Sector
										data={sectors}
										fieldName={formFieldName}
										isError={isErrorGetSectors}
										isLoading={isLoadingGetSectors}
									/>
									<Line
										data={lines}
										fieldName={formFieldName}
										isError={isErrorGetLines}
										isLoading={isLoadingGetLines}
									/>
									<Workstation
										data={workstations}
										fieldName={formFieldName}
										isError={isErrorGetWorkstations}
										isLoading={isLoadingGetWorkstations}
									/>
									<Activity
										data={activities}
										fieldName={formFieldName}
										isError={isErrorGetActivities}
										isLoading={isLoadingGetActivities}
									/>
								</Row>
							</Col>
						</Row>
					</Col>
					<Col span={24}>
						<Row justify="center">
							<Col lg={20} xl={14} xxl={14}>
								<Row gutter={[0, 40]}>
									<Evaluator fieldName={formFieldName} />
									<CollectionDate fieldName={formFieldName} />
									<AnalysisDate fieldName={formFieldName} />
								</Row>
							</Col>
						</Row>
					</Col>
					<Col span={24}>
						<Row justify="center">
							<Col lg={20} xl={14} xxl={14}>
								<Row gutter={[0, 40]}>
									<ActivityDescription fieldName={formFieldName} />
									<NumberExposed fieldName={formFieldName} />
									<CaptureInterval fieldName={formFieldName} />
								</Row>
							</Col>
						</Row>
					</Col>
				</Row>
			</Col>
			<Col span={24} style={{ marginTop: 15 }}>
				<Row justify="center">
					<Col>
						<Button type="primary" loading={isLoading} onClick={handleOnSubmit}>
							{I18n.get('Next')}
						</Button>
					</Col>
				</Row>
			</Col>
		</Row>
	);
}
